import { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, fmtDate } from "@/components/Bits";

const AUD = [["shared", "Both audiences"], ["cpg", "CPG only"], ["hospitality", "Hospitality only"]];
const inp = "h-10 w-full rounded-lg border border-input bg-white px-3 text-sm";

const Form = ({ kind, init, onDone }) => {
  const blank = kind === "drops" ? { title: "", body: "", video_url: "", audience: "shared", early_access_until: "", published: true }
    : { title: "", body: "", audience: "shared", publish_from: "", publish_to: "", early_access_until: "", repeat_yearly: true };
  const [f, setF] = useState(init || blank);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...blank, ...f };
      delete body.id; delete body.created_at; delete body.updated_at;
      if (init?.id) await api.put(`/admin/${kind}/${init.id}`, body); else await api.post(`/admin/${kind}`, body);
      toast.success("Saved"); setF(blank); onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="rounded-2xl border border-border bg-card p-5 grid gap-3 sm:grid-cols-2 mb-6" data-testid={`${kind}-form`}>
      <input required value={f.title} onChange={set("title")} placeholder="Title" data-testid={`${kind}-title`} className={`${inp} sm:col-span-2`} />
      <textarea rows={kind === "drops" ? 5 : 3} value={f.body} onChange={set("body")} placeholder={kind === "drops" ? "Written piece (markdown supported)" : "The idea, in a few lines"} data-testid={`${kind}-body`} className="sm:col-span-2 rounded-lg border border-input bg-white px-3 py-2 text-sm" />
      {kind === "drops" && <input value={f.video_url} onChange={set("video_url")} placeholder="Video link (YouTube, Vimeo or any URL), optional" data-testid="drops-video" className={`${inp} sm:col-span-2`} />}
      {kind !== "drops" && <><label className="text-xs">Show from<input required type="date" value={f.publish_from} onChange={set("publish_from")} data-testid="ideas-from" className={inp} /></label>
        <label className="text-xs">Show until<input required type="date" value={f.publish_to} onChange={set("publish_to")} data-testid="ideas-to" className={inp} /></label></>}
      {kind !== "drops" && <label className="sm:col-span-2 flex items-center gap-2 text-xs"><input type="checkbox" checked={!!f.repeat_yearly} onChange={(e) => setF({ ...f, repeat_yearly: e.target.checked })} data-testid="ideas-repeat" />Repeat every year on the same dates</label>}
      <label className="text-xs">Audience<select value={f.audience} onChange={set("audience")} data-testid={`${kind}-audience`} className={inp}>{AUD.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
      <div className="sm:col-span-2 flex justify-end gap-2">{init && <button type="button" onClick={onDone} className="text-sm text-muted-foreground px-3">Cancel</button>}<button className="rounded-full bg-navy text-white px-5 py-2 text-sm" data-testid={`${kind}-save`}>{init ? "Save changes" : kind === "drops" ? "Publish drop" : "Schedule idea"}</button></div>
    </form>
  );
};

const Section = ({ kind, title, hint }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/admin/${kind}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="mb-12">
      <h2 className="font-serif uppercase text-xl text-navy">{title}</h2>
      <p className="text-xs text-muted-foreground mb-3">{hint}</p>
      <Form key={editing?.id || "new"} kind={kind} init={editing} onDone={() => { setEditing(null); load(); }} />
      <ul className="space-y-2">{items.map((x) => (
        <li key={x.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm" data-testid={`${kind}-item-${x.id}`}>
          <span><span className="font-medium">{x.title}</span> <span className="text-xs text-muted-foreground">· {AUD.find((a) => a[0] === x.audience)?.[1]}{x.publish_from ? ` · ${fmtDate(x.publish_from)} to ${fmtDate(x.publish_to)}` : ` · ${fmtDate(x.created_at)}`}{x.early_access_until && ` · Pro early until ${fmtDate(x.early_access_until)}`}</span></span>
          <span className="flex gap-3"><button onClick={() => setEditing(x)} aria-label="Edit" data-testid={`${kind}-edit-${x.id}`}><Pencil className="h-4 w-4 text-muted-foreground hover:text-pinkb" /></button>
            <button onClick={() => window.confirm("Delete?") && api.delete(`/admin/${kind}/${x.id}`).then(load)} aria-label="Delete" data-testid={`${kind}-delete-${x.id}`}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-rose-600" /></button></span>
        </li>
      ))}</ul>
    </section>
  );
};

export default function AdminContent() {
  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow="Content" title="Seasonal ideas & content drops." />
      <Section kind="seasonal-ideas" title="Seasonal ideas" hint="Schedule ideas for CPG, hospitality or both. They show to members between the two dates." />
      <Section kind="drops" title="Content drops" hint="Videos and written pieces that appear in members' feeds. Set an early-access date to give Pro members first look." />
    </div>
  );
}
